/**
 * AdminErrors — recent backend exceptions, grouped + drillable.
 *
 * Each row is one (exc_type × path) cluster with hit count, first/last
 * seen, and the last stack trace. Click a row to expand its traceback.
 * This is the page you check when /admin/health shows the error tile in
 * red, or after a deploy to confirm nothing new is breaking.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import {
  AlertTriangle, ChevronDown, ChevronRight, Clock, Loader2, RefreshCw,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/apiBase';
import useHideCookieBanner from '../utils/useHideCookieBanner';
import AdminReadinessBanner from '../components/AdminReadinessBanner';
import AdminSubNav from '../components/AdminSubNav';


const WINDOWS = [
  { hours: 1,   label: '1h' },
  { hours: 24,  label: '24h' },
  { hours: 168, label: '7d' },
  { hours: 720, label: '30d' },
];


const AdminErrors = () => {
  const { user, loading: authLoading } = useAuth();
  useHideCookieBanner();
  const [data, setData] = useState(null);
  const [hours, setHours] = useState(24);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true); else setRefreshing(true);
    try {
      const r = await axios.get(`${API}/admin/errors?hours=${hours}`, { withCredentials: true });
      setData(r.data);
    } catch (_e) {
      // soft-fail
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [hours]);

  useEffect(() => { load(true); }, [load]);

  if (authLoading) return <div className="pt-24 text-center text-[#6b7280]">Loading…</div>;
  if (!user || user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20 pb-12" data-testid="admin-errors-page">
      <AdminReadinessBanner />
      <AdminSubNav />

      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7]">ADMIN · ERRORS</span>
            <h1 className="font-satoshi text-3xl font-black text-[#0f1115] flex items-center gap-2">
              <AlertTriangle size={22} className="text-[#e11d48]" />
              Backend Errors
            </h1>
            <p className="text-sm text-[#6b7280]">Unhandled 5xx exceptions captured globally</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 bg-white border border-[#e5e7eb] p-1">
              {WINDOWS.map((w) => (
                <button
                  key={w.hours}
                  onClick={() => setHours(w.hours)}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                    hours === w.hours ? 'bg-[#0f1115] text-white' : 'text-[#4b5563] hover:bg-[#f4f5f7]'
                  }`}
                  data-testid={`errors-window-${w.hours}`}
                >
                  {w.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => load(false)}
              className="px-3 py-2 text-sm font-semibold bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7] transition-colors inline-flex items-center gap-1.5"
              data-testid="errors-refresh"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Updating…' : 'Refresh'}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="bg-white border border-[#e5e7eb] p-8 text-center">
            <Loader2 size={24} className="animate-spin mx-auto text-[#6b7280]" />
          </div>
        ) : (
          <>
            {/* Big number */}
            <div className="bg-white border-l-4 border border-[#e5e7eb] p-5 mb-5" style={{ borderLeftColor: data?.total_errors > 0 ? '#e11d48' : '#10b981' }}>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#6b7280]">Total errors</div>
              <div className="font-satoshi text-4xl font-black tabular-nums text-[#0f1115]" data-testid="errors-total">
                {data?.total_errors?.toLocaleString() || 0}
              </div>
              <div className="text-xs text-[#9ca3af] mt-1">across {data?.groups?.length || 0} unique groups · last {data?.window_hours}h</div>
            </div>

            {(!data?.groups || data.groups.length === 0) ? (
              <div className="bg-white border border-[#e5e7eb] p-8 text-center text-[#10b981]" data-testid="errors-empty">
                ✓ No 5xx errors recorded in this window.
              </div>
            ) : (
              <div className="bg-white border border-[#e5e7eb] divide-y divide-[#e5e7eb]" data-testid="errors-list">
                {data.groups.map((g, i) => {
                  const isExpanded = expanded === i;
                  return (
                    <div key={i} data-testid={`errors-row-${i}`}>
                      <button
                        type="button"
                        onClick={() => setExpanded(isExpanded ? null : i)}
                        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-[#f4f5f7] text-left"
                      >
                        {isExpanded ? <ChevronDown size={14} className="text-[#6b7280]" /> : <ChevronRight size={14} className="text-[#6b7280]" />}
                        <span className="font-mono text-xs bg-[#fef2f2] text-[#991b1b] px-2 py-0.5 font-bold flex-shrink-0">
                          {g.count}×
                        </span>
                        <span className="font-bold text-sm text-[#0f1115] flex-shrink-0">{g.exc_type}</span>
                        <span className="font-mono text-xs text-[#6b7280] truncate flex-1">{g.method} {g.path}</span>
                        <span className="text-[11px] text-[#9ca3af] flex items-center gap-1 flex-shrink-0">
                          <Clock size={10} /> {new Date(g.last_seen).toLocaleTimeString()}
                        </span>
                      </button>
                      {isExpanded && (
                        <div className="px-4 py-3 bg-[#0f1115] text-[#e5e7eb] text-xs font-mono overflow-x-auto" data-testid={`errors-trace-${i}`}>
                          <div className="text-[#fbbf24] mb-2">{g.last_message}</div>
                          {g.last_traceback ? (
                            <pre className="whitespace-pre-wrap leading-relaxed text-[#cbd5e1]">{g.last_traceback}</pre>
                          ) : (
                            <div className="text-[#6b7280] italic">no traceback (HTTPException raised explicitly)</div>
                          )}
                          <div className="text-[10px] text-[#6b7280] mt-3">
                            first seen {new Date(g.first_seen).toLocaleString()} · last seen {new Date(g.last_seen).toLocaleString()}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminErrors;
